import React, { useEffect, useState } from "react";
import { Modal, Button, Radio, Flex } from "antd";
import "./SelectPaymentMethodModal.scss";
import { mixPostV1 } from "../../services/mixin";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import FormCardStripe from "./Tripe/FormCardStripe";

interface PaymentMethod {
  key: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  extra?: React.ReactNode;
}

interface Props {
  isShow: boolean;
  amount: number;
  planName: string;
  paymentMethods: PaymentMethod[];
  publishKey: string;
  typeId?: number | string;
  apiEndpoint?: string;
  savedCardApiEndpoint?: string;
  onCancel: () => void;
  onSelectCard: () => void;
  onPaymentSuccess?: () => void;
}

const SelectPaymentMethodModal: React.FC<Props> = ({
  isShow,
  amount,
  planName,
  paymentMethods,
  publishKey = "",
  typeId,
  apiEndpoint,
  savedCardApiEndpoint,
  onCancel,
  onSelectCard,
  onPaymentSuccess,
}) => {
  const [selected, setSelected] = useState<string>("");
  // State cho form card

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleOk = async () => {
    if (selected === "card") {
      onCancel();
      onSelectCard();
    } else if (selected) {
      const card = paymentMethods.find((m) => m.key === selected);
      if (!card) return;
      setLoading(true);
      setMessage(null);
      try {
        const endpoint =
          savedCardApiEndpoint ||
          (typeId
            ? "/api/webAdmin/upgradePackedByCardOld.php"
            : "/api/webAdmin/paymentPackedByCardOld.php");
        const payload: any = { card_id: card.key };
        if (typeId) payload.type_id = typeId;
        const res = await mixPostV1(endpoint, payload);
        if (res.status === 1) {
          setMessage("Payment successful!");
          setTimeout(() => {
            setLoading(false);
            setMessage(null);
            if (typeof onPaymentSuccess === "function") onPaymentSuccess();
            onCancel();
          }, 1200);
        } else {
          setMessage(res.message || "Payment failed!");
          setLoading(false);
        }
      } catch (e) {
        setMessage("Payment failed!");
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      open={isShow}
      onCancel={onCancel}
      footer={null}
      centered
      className="rs-modal w-480 modal-select-payment-method"
    >
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <div>
            <b>Payment Due:</b> {amount}
          </div>
          <div>
            <b>Your plan:</b> {planName}
          </div>
        </Flex>
        <div style={{ fontWeight: 600, marginTop: 8 }}>Payment method</div>
        <Radio.Group
          onChange={(e) => setSelected(e.target.value)}
          value={selected}
          style={{ width: "100%" }}
        >
          <div className="spm-list">
            {paymentMethods.map((method) => (
              <React.Fragment key={method.key}>
                <div
                  className={`spm-item${
                    selected === method.key ? " selected" : ""
                  }`}
                  onClick={() => setSelected(method.key)}
                >
                  <Radio value={method.key} className="spm-radio" />
                  <div className="spm-info">
                    {method.icon && (
                      <span className="spm-logo">{method.icon}</span>
                    )}
                    <div className="spm-main">
                      {method.key !== "card" ? (
                        (() => {
                          const [brand, number, ...ownerArr] =
                            method.label.split(" ");
                          return (
                            <>
                              <span className="spm-label">{brand}</span>
                              <span className="spm-number">{number}</span>
                              <span className="spm-owner">
                                {ownerArr.join(" ")}
                              </span>
                            </>
                          );
                        })()
                      ) : (
                        <span className="spm-label">{method.label}</span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Hiện form nhập thẻ nếu chọn "card" */}
                {method.key === "card" && selected === "card" && (
                  <div className="spm-card-form">
                    {publishKey !== null && (
                      <Elements stripe={loadStripe(publishKey)}>
                        <FormCardStripe
                          amount={amount}
                          onCancel={onCancel}
                          selected={selected}
                          setMessage={setMessage}
                          loading={loading}
                          onPaymentSuccess={onPaymentSuccess}
                          typeId={typeId}
                          apiEndpoint={apiEndpoint}
                        />
                      </Elements>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Radio.Group>
        {selected !== "card" && (
          <Flex gap={16} justify="center" style={{ marginTop: 24 }}>
            <Button
              className="spm-cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            {selected && (
              <Button
                type="primary"
                className="spm-payment-btn"
                onClick={handleOk}
                loading={loading}
                disabled={loading}
              >
                Payment {amount}
              </Button>
            )}
          </Flex>
        )}
        {message && (
          <div
            style={{
              color: message === "Payment successful!" ? "green" : "red",
              textAlign: "center",
              marginTop: 8,
            }}
          >
            {message}
          </div>
        )}
      </Flex>
    </Modal>
  );
};

export default SelectPaymentMethodModal;
